import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../components/admin/AdminLayout";
import { api, uploadApi } from "../../lib/api";

const defaultJournalTitle = "Polymath Journal of Humanities, Science & Technology";

export default function AdminJournalIssueForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: "",
    journal_title: defaultJournalTitle,
    volume: "",
    issue: "",
    publication_date: "",
    description: "",
    issn: "2786-9822",
    eissn: "3043-5382",
    publisher: "The Federal Polytechnic, Bida, Niger State, Nigeria",
    pdf_url: "",
    status: "published",
    is_featured: false,
  });
  const [coverImage, setCoverImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    async function load() {
      try {
        const data = await api(`/api/admin/journal-issues/${id}`);
        setForm({
          title: data.title || "",
          journal_title: data.journal_title || defaultJournalTitle,
          volume: data.volume || "",
          issue: data.issue || "",
          publication_date: data.publication_date ? String(data.publication_date).slice(0, 10) : "",
          description: data.description || "",
          issn: data.issn || "2786-9822",
          eissn: data.eissn || "3043-5382",
          publisher: data.publisher || "The Federal Polytechnic, Bida, Niger State, Nigeria",
          pdf_url: data.pdf_url || "",
          status: data.status || "published",
          is_featured: Boolean(data.is_featured),
        });
      } catch (err) {
        setError(err.message);
      }
    }
    load();
  }, [id]);

  function updateField(e) {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const data = new FormData();
      Object.entries(form).forEach(([key, value]) => data.append(key, key === "is_featured" ? (value ? "1" : "0") : value));
      if (coverImage) data.append("cover_image", coverImage);
      await uploadApi(id ? `/api/admin/journal-issues/${id}` : "/api/admin/journal-issues", data, id ? "PUT" : "POST");
      navigate("/adxm/journal-issues");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminLayout title={id ? "Edit Journal Issue" : "Create Journal Issue"} subtitle="Create classic Polymath journal records that display like the original journal archive design.">
      <form onSubmit={submit} className="mx-auto max-w-5xl rounded-3xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
        {error && <div className="mb-5 rounded-xl bg-red-50 p-4 font-semibold text-red-700">{error}</div>}

        <div className="mb-6 rounded-2xl border border-blue-100 bg-blue-50 p-5">
          <h2 className="text-lg font-black text-slate-950">Classic Journal Display Fields</h2>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            These fields control the public journal page hero, issue cards, ISSN/E-ISSN, publisher information and PDF download button.
          </p>
        </div>

        <div className="grid gap-5">
          <div>
            <label className="font-bold text-slate-700">Journal Title</label>
            <input name="journal_title" value={form.journal_title} onChange={updateField} placeholder={defaultJournalTitle} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>

          <div>
            <label className="font-bold text-slate-700">Issue Title</label>
            <input name="title" value={form.title} onChange={updateField} required placeholder="Vol. 3, No. 2 (November 2025)" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <label className="font-bold text-slate-700">Volume</label>
              <input name="volume" value={form.volume} onChange={updateField} placeholder="Vol. 3" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">Issue / Number</label>
              <input name="issue" value={form.issue} onChange={updateField} placeholder="No. 2" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">Publication Date</label>
              <input type="date" name="publication_date" value={form.publication_date} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            <div>
              <label className="font-bold text-slate-700">ISSN</label>
              <input name="issn" value={form.issn} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">E-ISSN</label>
              <input name="eissn" value={form.eissn} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            </div>
            <div>
              <label className="font-bold text-slate-700">Status</label>
              <select name="status" value={form.status} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          </div>

          <div>
            <label className="font-bold text-slate-700">Publisher</label>
            <input name="publisher" value={form.publisher} onChange={updateField} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>

          <div>
            <label className="font-bold text-slate-700">Issue PDF URL</label>
            <input name="pdf_url" value={form.pdf_url} onChange={updateField} placeholder="/journals/BIDA POLYMATH Vol 3 Issue 2 Nov 2025.pdf or uploaded PDF URL" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            <p className="mt-2 text-xs font-semibold text-slate-500">Paste an uploaded PDF path or external PDF link. This powers the public “Download PDF” button.</p>
          </div>

          <div>
            <label className="font-bold text-slate-700">Description</label>
            <textarea name="description" value={form.description} onChange={updateField} rows="5" className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
          </div>

          <div>
            <label className="font-bold text-slate-700">Cover Image</label>
            <input type="file" accept="image/*" onChange={(e) => setCoverImage(e.target.files?.[0] || null)} className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3" />
            <p className="mt-2 text-xs font-semibold text-slate-500">Use a portrait journal cover like the original Polymath page.</p>
          </div>

          <label className="flex items-center gap-3 font-bold text-slate-700">
            <input type="checkbox" name="is_featured" checked={form.is_featured} onChange={updateField} /> Feature this issue as latest/hero issue
          </label>

          <div className="flex flex-col gap-3 sm:flex-row">
            <button disabled={saving} className="rounded-xl bg-blue-700 px-6 py-4 font-black text-white disabled:opacity-60">{saving ? "Saving..." : "Save Issue"}</button>
            <button type="button" onClick={() => navigate("/adxm/journal-issues")} className="rounded-xl border border-slate-300 px-6 py-4 font-black text-slate-700">Cancel</button>
          </div>
        </div>
      </form>
    </AdminLayout>
  );
}
